import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import { RELEASE_PRODUCT_LABELS, deliveryProblem, productErrorMessage, todayInput } from "../../lib/product";
import { Footer, useRequest } from "./ProductDialogs";
import { SelectField } from "../tickets/TicketDialogs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

// --- Create or rename a release (while it is planned) ----------------------------------------------------------------------------
export function ReleaseFormDialog({ release, open, onOpenChange, onDone }) {
  const req = useRequest();
  const editing = !!release;
  const [draft, setDraft] = useState({ product: "crm", label: "", notes: "" });
  useEffect(() => {
    if (open) { setDraft({ product: release?.product || "crm", label: release?.label || "", notes: release?.notes || "" }); req.setError(""); }
  }, [open, release]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = !editing || draft.label.trim() !== release.label || draft.notes.trim() !== (release.notes || "");
  const submit = () => req.run(() => (editing
    ? api.patch(`/product/releases/${release.id}`, { expected_version: release.version, label: draft.label.trim(), notes: draft.notes.trim() })
    : api.post("/product/releases", { product: draft.product, label: draft.label.trim(), notes: draft.notes.trim() })), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier la version" : "Nouvelle version"}</DialogTitle>
          <DialogDescription>{editing ? release.number : "Préparez la version ; elle ne sera « livrée » qu'avec sa preuve de déploiement."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!editing && <SelectField id="release-product" label="Logiciel *" value={draft.product} onChange={(v) => setDraft((d) => ({ ...d, product: v }))} options={RELEASE_PRODUCT_LABELS} />}
          <div className="space-y-1.5">
            <Label htmlFor="release-label">Numéro de version *</Label>
            <Input id="release-label" value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} maxLength={40} placeholder="2026.10.3" data-testid="release-label" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="release-notes">Notes de version</Label>
            <Textarea id="release-notes" value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} rows={5} maxLength={4000} data-testid="release-notes"
              placeholder="Ce qui change pour les équipes (internes : rien n'est envoyé aux pharmacies)" />
          </div>
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={draft.label.trim().length < 1 || !changed}
          label={editing ? "Enregistrer" : "Créer la version"} testid="release-form-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Deliver: the proof, the day, and whether the items in test become « livré » with it ------------------------------------------
export function DeliverDialog({ release, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [form, setForm] = useState({ deployRef: "", deployedAt: todayInput(), notes: "", deliverItems: true });
  useEffect(() => {
    if (open && release) { setForm({ deployRef: "", deployedAt: todayInput(), notes: release.notes || "", deliverItems: true }); req.setError(""); }
  }, [open, release]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!release) return null;
  const problem = deliveryProblem(form);
  const inTest = (release.items || []).filter((i) => i.status === "in_test").length;
  const submit = () => req.run(() => api.post(`/product/releases/${release.id}/deliver`, {
    expected_version: release.version, deploy_ref: form.deployRef.trim(), deployed_at: form.deployedAt, deliver_items: form.deliverItems,
    ...(form.notes.trim() !== (release.notes || "") ? { notes: form.notes.trim() } : {}),
  }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Livrer la version {release.label}</DialogTitle>
          <DialogDescription>Une personne déclare la livraison et en donne la preuve. Elle ne se défait pas : une erreur se corrige par une note datée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="deliver-ref">Preuve de déploiement *</Label>
            <Input id="deliver-ref" value={form.deployRef} onChange={(e) => setForm((f) => ({ ...f, deployRef: e.target.value }))} maxLength={120} placeholder="abcdef1 ou v2026.10.3"
              className="font-mono" autoComplete="off" data-testid="deliver-ref" />
            <p className="text-xs text-slate-500">Le commit déployé (7 à 40 caractères hexadécimaux) ou l'étiquette git.
              {release.product === "crm" && " Pour le CRM, un commit identique à celui qui tourne est « confirmé en production »."}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deliver-day">Jour du déploiement *</Label>
            <Input id="deliver-day" type="date" value={form.deployedAt} max={todayInput()} onChange={(e) => setForm((f) => ({ ...f, deployedAt: e.target.value }))} className="max-w-[12rem]" data-testid="deliver-day" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deliver-notes">Notes de version</Label>
            <Textarea id="deliver-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} maxLength={4000} />
          </div>
          {(release.items || []).length > 0 && (
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox checked={form.deliverItems} onCheckedChange={(v) => setForm((f) => ({ ...f, deliverItems: !!v }))} className="mt-0.5" data-testid="deliver-items" />
              <span>Marquer « livrés » les éléments encore en test de cette version ({inTest}).
                <span className="block text-xs text-slate-500">Décochez pour les livrer un par un après vérification.</span></span>
            </label>
          )}
          {problem && <p className="text-xs text-slate-500" data-testid="deliver-problem">{problem}</p>}
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} disabled={!!problem} label="Livrer la version" testid="deliver-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- A dated correction of a delivered version -------------------------------------------------------------------------------------
export function CorrectionDialog({ release, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [text, setText] = useState("");
  useEffect(() => { if (open) { setText(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!release) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter une correction</DialogTitle>
          <DialogDescription>Une version livrée ne se modifie pas : la note est datée et signée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="correction-text">Correction *</Label>
          <Textarea id="correction-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000} data-testid="correction-text"
            placeholder="Le commit livré était abcdef1, pas abcdef2" />
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={() => req.run(() => api.post(`/product/releases/${release.id}/corrections`, { text: text.trim() }), onDone)}
          disabled={!text.trim()} label="Ajouter" testid="correction-submit" />
      </DialogContent>
    </Dialog>
  );
}

// --- Add an item in test to a version ---------------------------------------------------------------------------------------------
export function AttachItemDialog({ release, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    if (!open || !release) return;
    setRows(null); setLoadError(""); req.setError("");
    api.get("/product/items", { params: { status: "in_test", limit: 100 } })
      .then(({ data }) => setRows(data.items.filter((i) => !(release.items || []).some((x) => x.id === i.id))))
      .catch((e) => setLoadError(productErrorMessage(e, "Impossible de charger les éléments en test.")));
  }, [open, release]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!release) return null;
  const pick = (item) => req.run(() => api.post(`/product/releases/${release.id}/items`, { item_id: item.id }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter un élément à la version</DialogTitle>
          <DialogDescription>Seuls les éléments « en test » peuvent être rattachés ; un élément n'est prévu que dans une version à la fois.</DialogDescription>
        </DialogHeader>
        {!rows && !loadError && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Chargement…</p>}
        {loadError && <p className="text-sm text-red-600" role="alert">{loadError}</p>}
        {rows && rows.length === 0 && <p className="text-sm text-slate-500" data-testid="attach-empty">Aucun élément en test à rattacher.</p>}
        {rows && rows.length > 0 && (
          <ul className="max-h-64 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="attach-list">
            {rows.map((i) => (
              <li key={i.id}>
                <button type="button" disabled={req.busy} onClick={() => pick(i)} data-testid={`attach-pick-${i.number}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none disabled:opacity-60">
                  <span className="block text-xs text-slate-500 tabular-nums">{i.number}</span>
                  <span className="block text-slate-900 break-words">{i.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {req.error && <p className="text-sm text-red-600" role="alert" data-testid="product-dialog-error">{req.error}</p>}
      </DialogContent>
    </Dialog>
  );
}

// --- Functional check of a delivered item (never blocking) ------------------------------------------------------------------------
export function CheckDialog({ item, open, onOpenChange, onDone }) {
  const req = useRequest();
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setNote(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!item) return null;
  const submit = () => req.run(() => api.post(`/product/items/${item.id}/functional-check`, { expected_version: item.version, ...(note.trim() ? { note: note.trim() } : {}) }), onDone);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Marquer comme vérifié</DialogTitle>
          <DialogDescription>{item.number} — le correctif a été constaté en conditions réelles. Cela ne bloque jamais rien.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="check-note">Note (facultatif)</Label>
          <Textarea id="check-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} data-testid="check-note" placeholder="Testé en pharmacie pilote" />
        </div>
        <Footer busy={req.busy} error={req.error} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Marquer comme vérifié" testid="check-submit" />
      </DialogContent>
    </Dialog>
  );
}
